// src/pages/AdminParticipants.tsx — participants list with search + source filter.
import { useMemo, useState } from "react";
import { useAdminParticipants } from "@/lib/queries";
import { Card, PageHeader, Spinner, EmptyState, ErrorBanner } from "@/components/ui";

const SOURCE_APPS = ["", "the-edge", "biotrack", "peptide-calc", "life-suite", "pulse"];

export function AdminParticipants() {
  const [sourceApp, setSourceApp] = useState("");
  const [search, setSearch] = useState("");
  const { data, isLoading, error } = useAdminParticipants(sourceApp || undefined);

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (p) =>
        p.email.toLowerCase().includes(q) ||
        (p.displayName ?? "").toLowerCase().includes(q) ||
        p.userId.toLowerCase().includes(q)
    );
  }, [data, search]);

  return (
    <div className="space-y-6">
      <PageHeader title="Participants" subtitle="Canonical PULSE user roster." />

      <div className="flex flex-wrap gap-3">
        <input
          aria-label="search participants"
          placeholder="Search email / name / id"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
        />
        <select
          aria-label="filter source app"
          value={sourceApp}
          onChange={(e) => setSourceApp(e.target.value)}
          className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
        >
          {SOURCE_APPS.map((s) => (
            <option key={s} value={s}>
              {s || "all sources"}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <ErrorBanner message={(error as Error).message} />
      ) : isLoading ? (
        <Spinner />
      ) : filtered.length === 0 ? (
        <EmptyState title="No participants match" />
      ) : (
        <Card>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-ls-muted">
                <th className="py-1">Email</th>
                <th className="py-1">Name</th>
                <th className="py-1">Source</th>
                <th className="py-1">Flags</th>
                <th className="py-1">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-white/5 text-ls-fg">
                  <td className="py-1.5">{p.email}</td>
                  <td className="py-1.5">{p.displayName ?? "—"}</td>
                  <td className="py-1.5 text-ls-muted">{p.sourceApp}</td>
                  <td className="py-1.5">
                    {p.isAdmin ? <span className="mr-1 text-ls-accent">admin</span> : null}
                    {p.isMinor ? <span className="text-amber-400">minor</span> : null}
                    {!p.isAdmin && !p.isMinor ? <span className="text-ls-muted">—</span> : null}
                  </td>
                  <td className="py-1.5 text-ls-muted">
                    {p.lastSeenAt ? new Date(p.lastSeenAt).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
